import { Suspense, useState, useMemo, useCallback, useEffect, useTransition, type Dispatch, type SetStateAction } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryLoader, usePreloadedQuery, useMutation, graphql } from 'react-relay';
import { format, addDays, addMonths, subMonths } from 'date-fns';
import { Plus, Upload, Download } from 'lucide-react';
import { parseLocalDate } from '@/utils/date';
import { Button } from '@/components/ui/Button';
import { DaysheetlyButton } from '@/components/ui/DaysheetlyButton';
import { FadeIn } from '@/components/animations/FadeIn';
import { EmptyState } from '@/components/emptyState/EmptyState';
import { Skeleton, TimelineSkeleton } from '@/components/loading/LoadingState';
import {
  TimelineGrid,
  type TimelineEntry,
  type TimelineUser,
} from '@/features/daySheetly/components/timeline/TimelineGrid';
import {
  DaySheetlyFiltersComponent,
  type DaySheetlyFilters,
} from '@/features/daySheetly/components/DaySheetlyFilters';
import { MobileDaySheetlyFilters } from '@/features/daySheetly/components/MobileDaySheetlyFilters';
import { TaskDialog, type TaskFormData } from '@/features/daySheetly/components/TaskDialog';
import { TaskContextMenu } from '@/features/daySheetly/components/TaskContextMenu';
import { ImportJsonDialog } from '@/features/daySheetly/components/ImportJsonDialog';
import { ExportJsonDialog } from '@/features/daySheetly/components/ExportJsonDialog';
import { DeleteConfirmation } from '@/components/ui/DeleteConfirmation';
import { toast } from 'sonner';
import type { PreloadedQuery } from 'react-relay';
import type { DaySheetlyPageQuery as DaySheetlyPageQueryType } from '@/__generated__/DaySheetlyPageQuery.graphql.ts';
import type { DaySheetlyPageCreateMutation as DaySheetlyPageCreateMutationType } from '@/__generated__/DaySheetlyPageCreateMutation.graphql.ts';
import type { DaySheetlyPageUpdateMutation as DaySheetlyPageUpdateMutationType } from '@/__generated__/DaySheetlyPageUpdateMutation.graphql.ts';
import type { DaySheetlyPageDeleteMutation as DaySheetlyPageDeleteMutationType } from '@/__generated__/DaySheetlyPageDeleteMutation.graphql.ts';

const daySheetlyPageQuery = graphql`
  query DaySheetlyPageQuery(
    $startDate: String
    $endDate: String
    $projectId: ID
    $billingStatus: BillingStatus
  ) {
    projects {
      id
      name
      color
      hourlyRate
      currency
    }
    tasks(
      startDate: $startDate
      endDate: $endDate
      projectId: $projectId
      billingStatus: $billingStatus
    ) {
      id
      title
      description
      date
      endDate
      durationMinutes
      hourlyRate
      currency
      billingStatus
      project {
        id
        name
        color
      }
      members {
        id
        name
      }
    }
    viewer {
      id
      name
      email
      activeWorkspaceId
      workspaces {
        id
        name
        members {
          id
          role
          user {
            id
            name
            email
          }
        }
      }
    }
    workspaceBilling {
      effectivePlan
    }
  }
`;

const createTaskMutation = graphql`
  mutation DaySheetlyPageCreateMutation(
    $title: String!
    $description: String
    $date: String!
    $duration: String!
    $projectId: ID!
    $hourlyRate: Float
    $currency: Currency
    $billingStatus: BillingStatus
    $endDate: String
    $memberIds: [ID!]
  ) {
    createTask(
      title: $title
      description: $description
      date: $date
      duration: $duration
      projectId: $projectId
      hourlyRate: $hourlyRate
      currency: $currency
      billingStatus: $billingStatus
      endDate: $endDate
      memberIds: $memberIds
    ) {
      id
      title
      description
      date
      endDate
      durationMinutes
      hourlyRate
      currency
      billingStatus
      project {
        id
        name
        color
      }
      members {
        id
        name
      }
    }
  }
`;

const updateTaskMutation = graphql`
  mutation DaySheetlyPageUpdateMutation(
    $id: ID!
    $title: String
    $description: String
    $date: String
    $endDate: String
    $duration: String
    $projectId: ID
    $hourlyRate: Float
    $currency: Currency
    $billingStatus: BillingStatus
    $memberIds: [ID!]
  ) {
    updateTask(
      id: $id
      title: $title
      description: $description
      date: $date
      endDate: $endDate
      duration: $duration
      projectId: $projectId
      hourlyRate: $hourlyRate
      currency: $currency
      billingStatus: $billingStatus
      memberIds: $memberIds
    ) {
      id
      title
      description
      date
      endDate
      durationMinutes
      hourlyRate
      currency
      billingStatus
      project {
        id
        name
        color
      }
      members {
        id
        name
      }
    }
  }
`;

const deleteTaskMutation = graphql`
  mutation DaySheetlyPageDeleteMutation($id: ID!) {
    deleteTask(id: $id)
  }
`;

export function DaySheetlyPage() {
  const [searchParams] = useSearchParams();
  const shouldOpenAddTask = searchParams.get('addTask') === 'true';

  const defaultDates = useMemo(() => {
    const today = new Date();
    const bufferBefore = 17;
    const bufferAfter = 10;
    return {
      startDate: format(addDays(today, -bufferBefore), 'yyyy-MM-dd'),
      endDate: format(addDays(today, bufferAfter), 'yyyy-MM-dd'),
      referenceDate: format(today, 'yyyy-MM-dd'),
    };
  }, []);

  const [filters, setFilters] = useState<DaySheetlyFilters>({
    startDate: defaultDates.startDate,
    endDate: defaultDates.endDate,
    projectId: null,
    billingStatus: null,
  });

  const [queryRef, loadQuery] = useQueryLoader<DaySheetlyPageQueryType>(daySheetlyPageQuery);
  const [, startTransition] = useTransition();

  useEffect(() => {
    startTransition(() => {
      loadQuery({
        startDate: filters.startDate,
        endDate: filters.endDate,
        projectId: filters.projectId,
        billingStatus: filters.billingStatus,
      });
    });
  }, [filters.startDate, filters.endDate, filters.projectId, filters.billingStatus, loadQuery, startTransition]);

  if (!queryRef) {
    return (
      <div className="flex h-full min-w-0 flex-col">
        <FadeIn>
          <div className="px-4 pt-4 sm:px-6 sm:pt-6">
            <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">Day Sheetly</h1>
            <p className="hidden text-sm text-muted-foreground sm:block">
              Visualize and manage your tasks on the timeline.
            </p>
          </div>
        </FadeIn>
        <FadeIn
          delay={0.05}
          className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-4 pt-3 sm:px-6 sm:pb-6 sm:pt-4"
        >
          <div className="flex h-full min-h-0 flex-col gap-2 lg:gap-4">
            <div className="flex flex-wrap items-center gap-2 rounded-md border bg-background px-4 py-3">
              <Skeleton className="h-10 w-56" />
              <Skeleton className="h-10 w-44" />
            </div>
            <div className="min-w-0 flex-1 overflow-hidden rounded-md border bg-background">
              <TimelineSkeleton />
            </div>
          </div>
        </FadeIn>
      </div>
    );
  }

  return (
    <div className="flex h-full min-w-0 flex-col">
      <FadeIn>
        <div className="px-4 pt-4 sm:px-6 sm:pt-6">
          <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">Day Sheetly</h1>
          <p className="hidden text-sm text-muted-foreground sm:block">
            Visualize and manage your tasks on the timeline.
          </p>
        </div>
      </FadeIn>

      <FadeIn
        delay={0.05}
        className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-4 pt-3 sm:px-6 sm:pb-6 sm:pt-4"
      >
        <Suspense
          fallback={
            <div className="flex h-full min-h-0 flex-col gap-2 lg:gap-4">
              <div className="flex flex-wrap items-center gap-2 rounded-md border bg-background px-4 py-3">
                <Skeleton className="h-10 w-56" />
                <Skeleton className="h-10 w-44" />
              </div>
              <div className="min-w-0 flex-1 overflow-hidden rounded-md border bg-background">
                <TimelineSkeleton />
              </div>
            </div>
          }
        >
          <DaySheetlyPageContent
            queryRef={queryRef}
            filters={filters}
            defaultDateRange={defaultDates}
            onFiltersChange={setFilters}
            initialOpenAddTask={shouldOpenAddTask}
            referenceDate={defaultDates.referenceDate}
            refetch={() =>
              loadQuery(
                {
                  startDate: filters.startDate,
                  endDate: filters.endDate,
                  projectId: filters.projectId,
                  billingStatus: filters.billingStatus,
                },
                { fetchPolicy: 'network-only' },
              )
            }
          />
        </Suspense>
      </FadeIn>
    </div>
  );
}

/**
 * Inner component that owns the data fetch.
 * useLazyLoadQuery suspends, so we wrap this in <Suspense> at the parent and
 * show a skeleton while it loads. Without this boundary the timeline area
 * would render blank (no spinner) while the network request is in flight.
 */
function DaySheetlyPageContent({
  queryRef,
  filters,
  defaultDateRange,
  onFiltersChange,
  initialOpenAddTask,
  referenceDate,
  refetch,
}: {
  queryRef: PreloadedQuery<DaySheetlyPageQueryType>;
  filters: DaySheetlyFilters;
  defaultDateRange: Pick<DaySheetlyFilters, 'startDate' | 'endDate'>;
  // Accepts either a value or an updater fn — scroll-edge lazy-load uses
  // the updater form so it can extend the range without a stale read.
  onFiltersChange: Dispatch<SetStateAction<DaySheetlyFilters>>;
  initialOpenAddTask: boolean;
  referenceDate: string;
  refetch: () => void;
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(initialOpenAddTask);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskFormData | null>(null);
  const [taskDialogMode, setTaskDialogMode] = useState<'auto' | 'edit'>('auto');
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; entryId: string } | null>(
    null,
  );
  const [deleteConfirmation, setDeleteConfirmation] = useState<string | null>(null);
  /**
   * Local override of each task's `date` while a drag-to-move mutation is
   * in flight. The card moves to the new day instantly (no full timeline
   * refetch) and is cleared once the server confirms the move — Relay's
   * normalized store then holds the canonical new date.
   */
  const [optimisticMoves, setOptimisticMoves] = useState<Map<string, string>>(
    () => new Map(),
  );

  const data = usePreloadedQuery<DaySheetlyPageQueryType>(daySheetlyPageQuery, queryRef);
  const effectivePlan = data.workspaceBilling?.effectivePlan === 'PRO' ? 'PRO' : 'FREE';

  const [commitCreate] = useMutation<DaySheetlyPageCreateMutationType>(createTaskMutation);
  const [commitUpdate] = useMutation<DaySheetlyPageUpdateMutationType>(updateTaskMutation);
  const [commitDelete] = useMutation<DaySheetlyPageDeleteMutationType>(deleteTaskMutation);

  const projectOptions = useMemo(
    () => data.projects.map((p) => ({
      id: p.id,
      name: p.name,
      color: p.color,
      hourlyRate: p.hourlyRate,
      currency: p.currency as 'BRL' | 'USD',
    })),
    [data.projects],
  );

  // For v2 imports we need member emails (the server matches by email); pull
  // them from the same workspace query that already lists the members.
  const emailAwareMembers = useMemo<
    { id: string; name: string; email: string }[]
  >(() => {
    if (!data.viewer) return [];
    const active = data.viewer.workspaces.find(
      (w) => w.id === data.viewer?.activeWorkspaceId,
    );
    if (!active) return [];
    return active.members.map((m) => ({
      id: m.user.id,
      name: m.user.name,
      email: m.user.email,
    }));
  }, [data.viewer]);

  // Resolve the source-of-truth member list from the active workspace.
  // Fallback chain: active-workspace members → previous (tasks) →
  // a synthetic "self" row using the viewer's own id+name so the timeline
  // is never empty when the user is authenticated. Showing at least one
  // row means the page never dead-ends in a "no tasks yet" state — the
  // user always has a place to drop a new entry.
  const userOptions = useMemo<TimelineUser[]>(() => {
    if (data.viewer) {
      const active = data.viewer.workspaces.find(
        (w) => w.id === data.viewer?.activeWorkspaceId,
      );
      if (active && active.members.length > 0) {
        return active.members.map((m) => ({
          id: m.user.id,
          name: m.user.name,
        }));
      }
      // Authenticated user with no workspace yet (or an empty workspace):
      // synthesize a single row representing themselves so the timeline
      // renders and they can click "Add Task" to seed it.
      if (data.viewer.name) {
        return [{ id: data.viewer.id ?? 'self', name: data.viewer.name }];
      }
    }
    const map = new Map<string, TimelineUser>();
    data.tasks.forEach((entry) => {
      entry.members.forEach((m) => {
        if (!map.has(m.id)) {
          map.set(m.id, { id: m.id, name: m.name });
        }
      });
    });
    return Array.from(map.values());
  }, [data.viewer, data.tasks]);

  // Map the Relay tasks payload to the TimelineGrid shape. The Timeline
  // entry's `date` is the inclusive start (matches the API); `endDate` stays
  // untouched on the server even after a drag (see handleEntryMove below).
  const entries = useMemo<TimelineEntry[]>(
    () =>
      data.tasks.map((entry) => ({
        id: entry.id,
        title: entry.title,
        date: (optimisticMoves.get(entry.id) ?? entry.date) as string,
        endDate: (entry.endDate ?? null) as string | null,
        durationMinutes: entry.durationMinutes,
        hourlyRate: entry.hourlyRate,
        currency: entry.currency as 'BRL' | 'USD',
        project: entry.project
          ? {
              id: entry.project.id,
              name: entry.project.name,
              color: entry.project.color ?? null,
            }
          : null,
        members: entry.members.map((m) => ({ id: m.id, name: m.name })),
      })),
    [data.tasks, optimisticMoves],
  );

  const openNewTask = useCallback(() => {
    setSelectedTask(null);
    setTaskDialogMode('auto');
    setIsDialogOpen(true);
  }, []);

  // Open the new-task dialog on mount if the URL says so (deep link from
  // Dashboard's "Add Task" shortcut).
  useEffect(() => {
    if (initialOpenAddTask) {
      openNewTask();
    }
  }, [initialOpenAddTask, openNewTask]);

  const handleTaskClick = useCallback((id: string) => {
    const entry = data.tasks.find((e) => e.id === id);
    if (!entry) return;
    setSelectedTask({
      id: entry.id,
      title: entry.title,
      description: entry.description ?? '',
      duration: formatDuration(entry.durationMinutes),
      projectId: entry.project?.id ?? '',
      date: entry.date as string,
      endDate: (entry.endDate ?? undefined) as string | undefined,
      hourlyRate: entry.hourlyRate,
      currency: entry.currency as 'BRL' | 'USD',
      billingStatus: entry.billingStatus as 'unbilled' | 'billed',
      memberIds: entry.members.map((m) => m.id),
    });
    setTaskDialogMode('auto');
    setIsDialogOpen(true);
  }, [data.tasks]);

  const handleTaskEdit = useCallback((id: string) => {
    const entry = data.tasks.find((e) => e.id === id);
    if (!entry) return;
    setSelectedTask({
      id: entry.id,
      title: entry.title,
      description: entry.description ?? '',
      duration: formatDuration(entry.durationMinutes),
      projectId: entry.project?.id ?? '',
      date: entry.date as string,
      endDate: (entry.endDate ?? undefined) as string | undefined,
      hourlyRate: entry.hourlyRate,
      currency: entry.currency as 'BRL' | 'USD',
      billingStatus: entry.billingStatus as 'unbilled' | 'billed',
      memberIds: entry.members.map((m) => m.id),
    });
    setTaskDialogMode('edit');
    setIsDialogOpen(true);
  }, [data.tasks]);

  const handleEntryMove = useCallback(
    (id: string, date: string) => {
      // Pass only `date`. The server's updateTask resolver preserves the
      // existing `endDate` when it isn't supplied — sending the timeline's
      // visual-exclusive end here would corrupt multi-day entries.
      // Apply the move locally so the card jumps to the new day column
      // instantly; the override is cleared when the mutation resolves.
      setOptimisticMoves((prev) => {
        const next = new Map(prev);
        next.set(id, date);
        return next;
      });
      commitUpdate({
        variables: { id, date },
        onError: (err) => {
          // Roll back the optimistic move on failure.
          setOptimisticMoves((prev) => {
            const next = new Map(prev);
            next.delete(id);
            return next;
          });
          toast.error(`Failed to move task: ${err.message}`);
        },
        onCompleted: () => {
          // Server confirmed. Relay's normalized store now holds the new
          // date on the task record; drop the local override so entries
          // reads from the canonical store value.
          setOptimisticMoves((prev) => {
            const next = new Map(prev);
            next.delete(id);
            return next;
          });
        },
      });
    },
    [commitUpdate],
  );

  const handleDelete = useCallback(
    (id: string) => {
      commitDelete({
        variables: { id },
        onError: (err) => {
          toast.error(`Failed to delete task: ${err.message}`);
        },
        onCompleted: () => {
          toast.success('Task deleted');
          setIsDialogOpen(false);
          setSelectedTask(null);
          setDeleteConfirmation(null);
          refetch();
        },
      });
    },
    [commitDelete, refetch],
  );

  const handleEntryContextMenu = useCallback(
    (id: string, x: number, y: number) => {
      setContextMenu({ x, y, entryId: id });
    },
    [],
  );

  // Toggl-style lazy load: when the timeline reports the user is close to
  // the left or right edge, extend the date range by ONE MONTH in that
  // direction (Toggl loads month-by-month — issue #12 follow-up). The new
  // range flows back through `onFiltersChange` → parent state → re-renders
  // the lazy-loaded query with the wider window.
  //
  // Issue #13: when the refetch fails (network error, server 500), we
  // surface a toast so the user isn't stuck staring at a perpetual
  // loading strip. TimelineGrid's watchdog timer would drop the strip
  // after 30s, but a toast gives a faster signal.
  const handleScrollEdge = useCallback(
    (edge: 'left' | 'right') => {
      onFiltersChange((prev) => {
        const start = parseLocalDate(prev.startDate);
        const end = parseLocalDate(prev.endDate);
        if (edge === 'left') {
          return { ...prev, startDate: format(subMonths(start, 1), 'yyyy-MM-dd') };
        }
        return { ...prev, endDate: format(addMonths(end, 1), 'yyyy-MM-dd') };
      });
    },
    [onFiltersChange],
  );

  const handleSave = useCallback(
    (formData: TaskFormData) => {
      const variables = {
        title: formData.title,
        description: formData.description || null,
        date: formData.date,
        endDate: formData.endDate || null,
        duration: formData.duration,
        projectId: formData.projectId,
        hourlyRate: formData.hourlyRate,
        currency: formData.currency,
        billingStatus: formData.billingStatus,
        memberIds: formData.memberIds,
      };
      // Don't close the dialog on error — the user needs to see the toast
      // and try again. onError keeps the dialog open with form values intact
      // so they can correct whatever the server complained about.
      if (formData.id) {
        commitUpdate({
          variables: { id: formData.id, ...variables },
          onError: (err) => {
            toast.error(`Failed to update task: ${err.message}`);
          },
          onCompleted: () => {
            setIsDialogOpen(false);
            setSelectedTask(null);
          },
        });
      } else {
        commitCreate({
          variables,
          onError: (err) => {
            toast.error(`Failed to create task: ${err.message}`);
          },
          onCompleted: () => {
            setIsDialogOpen(false);
            setSelectedTask(null);
            // The create mutation response isn't linked to the tasks
            // list in the Relay store, so without a refetch the new card
            // only shows up after a full reload (issue #33 — surfaced while
            // testing the Unassigned row: a freshly created unassigned task
            // never appeared anywhere). Delete already refetches for the
            // same reason.
            refetch();
          },
        });
      }
    },
    [commitCreate, commitUpdate, refetch],
  );

  const handleInlineSave = useCallback(
    (formData: TaskFormData) => {
      if (!formData.id) return;
      commitUpdate({
        variables: {
          id: formData.id,
          title: formData.title,
          description: formData.description || null,
          date: formData.date,
          endDate: formData.endDate || null,
          duration: formData.duration,
          projectId: formData.projectId,
          hourlyRate: formData.hourlyRate,
          currency: formData.currency,
          billingStatus: formData.billingStatus,
          memberIds: formData.memberIds,
        },
        onError: (err) => {
          toast.error(`Failed to update task: ${err.message}`);
        },
        onCompleted: () => {
          toast.success('Task updated');
        },
      });
    },
    [commitUpdate],
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 lg:gap-4">
      <div className="lg:hidden">
        <MobileDaySheetlyFilters
          filters={filters}
          defaultDateRange={defaultDateRange}
          onChange={onFiltersChange}
          projects={projectOptions}
          onImport={() => setIsImportDialogOpen(true)}
          onExport={() => setIsExportDialogOpen(true)}
        />
      </div>

      <div className="hidden rounded-md border bg-background px-4 py-3 lg:flex lg:flex-row lg:items-center lg:justify-between lg:gap-3">
        <DaySheetlyFiltersComponent
          filters={filters}
          defaultDateRange={defaultDateRange}
          onChange={onFiltersChange}
          projects={projectOptions}
        />
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            onClick={() => setIsImportDialogOpen(true)}
            size="sm"
            variant="outline"
            className="shrink-0"
          >
            <Upload className="mr-2 h-4 w-4" />
            Import JSON
          </Button>
          <Button
            onClick={() => setIsExportDialogOpen(true)}
            size="sm"
            variant="outline"
            className="shrink-0"
          >
            <Download className="mr-2 h-4 w-4" />
            Export JSON
          </Button>
          <DaysheetlyButton
            onClick={openNewTask}
            size="sm"
            className="shrink-0"
            startIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
          >
            Add Task
          </DaysheetlyButton>
        </div>
      </div>

      <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden rounded-md border bg-background">
        {userOptions.length === 0 ? (
          <EmptyState
            title="Sign in to view tasks"
            description="Use the login page to access the timeline."
          />
        ) : (
          <TimelineGrid
            entries={entries}
            users={userOptions}
            startDate={filters.startDate}
            endDate={filters.endDate}
            onEntryClick={handleTaskClick}
            onEntryMove={handleEntryMove}
            onEntryContextMenu={handleEntryContextMenu}
            onScrollEdge={handleScrollEdge}
          />
        )}
        <DaysheetlyButton
          type="button"
          size="icon"
          aria-label="Add Task"
          title="Add Task"
          onClick={openNewTask}
          className="absolute bottom-4 right-4 z-30 h-12 w-12 rounded-full p-0 shadow-lg lg:hidden"
        >
          <Plus className="h-5 w-5" aria-hidden="true" />
        </DaysheetlyButton>
      </div>

      <TaskDialog
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setSelectedTask(null);
          setTaskDialogMode('auto');
        }}
        task={selectedTask}
        projects={projectOptions}
        members={userOptions}
        currentUserId={data.viewer?.id ?? undefined}
        initialMode={taskDialogMode}
        onSave={handleSave}
        onInlineSave={handleInlineSave}
        onDelete={(id) => setDeleteConfirmation(id)}
      />

      <ImportJsonDialog
        isOpen={isImportDialogOpen}
        onClose={() => setIsImportDialogOpen(false)}
        projects={projectOptions}
        members={emailAwareMembers}
        currentUserId={data.viewer?.id ?? undefined}
        activeWorkspaceId={data.viewer?.activeWorkspaceId ?? undefined}
        effectivePlan={effectivePlan}
        fallbackDate={referenceDate}
        onImported={refetch}
      />

      <ExportJsonDialog
        isOpen={isExportDialogOpen}
        onClose={() => setIsExportDialogOpen(false)}
        projects={projectOptions}
        members={userOptions}
        referenceDate={referenceDate}
        effectivePlan={effectivePlan}
      />

      {contextMenu && (
        <TaskContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          onEdit={() => handleTaskEdit(contextMenu.entryId)}
          onDelete={() => setDeleteConfirmation(contextMenu.entryId)}
        />
      )}

      <DeleteConfirmation
        open={deleteConfirmation !== null}
        onOpenChange={(open) => !open && setDeleteConfirmation(null)}
        title="Delete task?"
        description="This action cannot be undone. The task will be permanently removed from your workspace."
        onConfirm={() => {
          if (deleteConfirmation) handleDelete(deleteConfirmation);
        }}
        testId="task-delete-confirmation"
        actionsTestId="task-delete-confirmation-actions"
      />
    </div>
  );
}

function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}
